import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

type AudioKind = 'click' | 'transition' | 'open';

type AudioContextValue = {
  enabled: boolean;
  toggle: () => void;
  play: (kind: AudioKind) => void;
};

const Ctx = createContext<AudioContextValue | null>(null);
const STORAGE_KEY = 'inkhaus.audio';

/**
 * Ambient track: real audio file (Mixkit royalty-free, "Delirium" by Eugenio
 * Mininni, drone music). Streamed via HTMLAudioElement so the browser handles
 * buffering and looping. The track + sound effects fade in / out at 0.18 vol
 * so they sit well under content.
 *
 * Sound effects (click, transition, open) stay procedural via Web Audio API
 * because we want them tiny and instant.
 *
 * Activation rule:
 *  - localStorage 'on'  → on, respect choice
 *  - localStorage 'off' → off, respect choice
 *  - no localStorage    → auto-enable on first user gesture (browsers require
 *                         user gesture to start audio anyway)
 */

// Chad Crouch — "Algorithms" (Arps), CC BY-NC 3.0. Hosted locally for
// reliability (same-origin, no remote stalls).
const TRACK_URL = '/ambient.mp3';

export function AudioProvider({ children }: { children: ReactNode }) {
  const [enabled, setEnabled] = useState(false);
  const ctxRef = useRef<AudioContext | null>(null);
  const audioElRef = useRef<HTMLAudioElement | null>(null);
  const fadeIntervalRef = useRef<number>(0);
  const explicitChoiceRef = useRef<boolean>(false);

  // Hydrate from localStorage
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'on') {
      explicitChoiceRef.current = true;
      setEnabled(true);
    } else if (stored === 'off') {
      explicitChoiceRef.current = true;
      setEnabled(false);
    }
  }, []);

  const ensureCtx = useCallback(() => {
    if (ctxRef.current) return ctxRef.current;
    const Ctor =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    const ctx = new Ctor();
    ctxRef.current = ctx;
    return ctx;
  }, []);

  const ensureAudioEl = useCallback(() => {
    if (audioElRef.current) return audioElRef.current;
    const el = new Audio(TRACK_URL);
    el.loop = true;
    // NOTE: no crossOrigin → browser does a plain GET (no CORS preflight).
    // FMA serves CC tracks without CORS headers; with crossOrigin set, audio
    // load is rejected silently.
    el.preload = 'auto';
    el.volume = 0;
    audioElRef.current = el;
    return el;
  }, []);

  const fadeTo = useCallback((target: number, ms: number, onDone?: () => void) => {
    const el = audioElRef.current;
    if (!el) return;
    window.clearInterval(fadeIntervalRef.current);
    const start = el.volume;
    const startTime = performance.now();
    fadeIntervalRef.current = window.setInterval(() => {
      const t = Math.min(1, (performance.now() - startTime) / ms);
      el.volume = start + (target - start) * t;
      if (t >= 1) {
        window.clearInterval(fadeIntervalRef.current);
        onDone?.();
      }
    }, 40);
  }, []);

  // Apply enabled state → play/pause + fade
  useEffect(() => {
    const el = ensureAudioEl();
    if (enabled) {
      el.play().catch(() => {
        /* user gesture required — handled by auto-activation listener */
      });
      fadeTo(0.08, 1800);
    } else {
      if (!el.paused) {
        fadeTo(0, 600, () => {
          el.pause();
        });
      }
    }
    return () => {
      window.clearInterval(fadeIntervalRef.current);
    };
  }, [enabled, ensureAudioEl, fadeTo]);

  // First-gesture auto-activation when user has NOT made an explicit choice
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (explicitChoiceRef.current) return;

    const activate = () => {
      if (explicitChoiceRef.current) return;
      // CRITICAL: call play() synchronously inside the user gesture frame,
      // otherwise Chrome/Safari silently refuse with no error → audio is
      // considered "started but muted" and subsequent toggles do nothing.
      const el = ensureAudioEl();
      el.play().catch(() => {
        /* user gesture mismatch — will retry on toggle */
      });
      setEnabled(true);
      cleanup();
    };
    const cleanup = () => {
      window.removeEventListener('pointerdown', activate);
      window.removeEventListener('keydown', activate);
      window.removeEventListener('touchstart', activate);
    };
    window.addEventListener('pointerdown', activate, { once: true });
    window.addEventListener('keydown', activate, { once: true });
    window.addEventListener('touchstart', activate, { once: true });

    return cleanup;
  }, []);

  const toggle = useCallback(() => {
    setEnabled((prev) => {
      const next = !prev;
      explicitChoiceRef.current = true;
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(STORAGE_KEY, next ? 'on' : 'off');
      }
      return next;
    });
  }, []);

  const play = useCallback(
    (kind: AudioKind) => {
      if (!enabled) return;
      const ctx = ensureCtx();
      if (!ctx) return;

      const t0 = ctx.currentTime;
      const gain = ctx.createGain();
      gain.connect(ctx.destination);

      switch (kind) {
        case 'click': {
          const o = ctx.createOscillator();
          o.type = 'square';
          o.frequency.setValueAtTime(2400, t0);
          o.frequency.exponentialRampToValueAtTime(1100, t0 + 0.04);
          gain.gain.setValueAtTime(0.04, t0);
          gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.07);
          o.connect(gain);
          o.start(t0);
          o.stop(t0 + 0.09);
          break;
        }
        case 'transition': {
          const bufferSize = 0.35 * ctx.sampleRate;
          const buf = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
          const ch = buf.getChannelData(0);
          for (let i = 0; i < bufferSize; i++) ch[i] = (Math.random() * 2 - 1) * 0.5;
          const src = ctx.createBufferSource();
          src.buffer = buf;
          const filter = ctx.createBiquadFilter();
          filter.type = 'lowpass';
          filter.frequency.setValueAtTime(260, t0);
          filter.frequency.exponentialRampToValueAtTime(1600, t0 + 0.22);
          gain.gain.setValueAtTime(0, t0);
          gain.gain.linearRampToValueAtTime(0.04, t0 + 0.05);
          gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.4);
          src.connect(filter).connect(gain);
          src.start(t0);
          src.stop(t0 + 0.45);
          break;
        }
        case 'open': {
          const o = ctx.createOscillator();
          o.type = 'sine';
          o.frequency.setValueAtTime(180, t0);
          o.frequency.exponentialRampToValueAtTime(55, t0 + 0.22);
          gain.gain.setValueAtTime(0.0001, t0);
          gain.gain.linearRampToValueAtTime(0.08, t0 + 0.02);
          gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.32);
          o.connect(gain);
          o.start(t0);
          o.stop(t0 + 0.34);
          break;
        }
      }
    },
    [enabled, ensureCtx],
  );

  const value = useMemo(() => ({ enabled, toggle, play }), [enabled, toggle, play]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAudio() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAudio must be inside <AudioProvider>');
  return v;
}
